import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { keys } from 'lodash';
import FormModal from '../FormModal.jsx';
import ManageParticipantsForm from './ManageParticipantsForm.jsx';
import {
  createEmployee,
  retrieveSimplifiedEmployees,
  selectRetrieveSimplifiedEmployeesState,
} from '../../modules/employees.js';
import { validateEmployee } from '../../containers/employees/utils/index.js';
import { mapFinalFormErrors } from '../../lib/utils.js';
import { selectCurrentOrganizationId } from '../../modules/auth.js';

const getChunkOfElements = (arr, start, size) => arr.slice(start, start + size);

const mapStateToProps = (state) => ({
  retrieveEmployeesPending:
    selectRetrieveSimplifiedEmployeesState(state).pending,
  organizationId: selectCurrentOrganizationId(state),
});

const mapDispatchToProps = { retrieveSimplifiedEmployees, createEmployee };

const ManageParticipantsModal = ({
  organizationId,
  onSubmit,
  showFormModal,
  employeeIds,
  onHideFormModal,
  additionalSearchParams,
  retrieveSimplifiedEmployees,
  retrieveEmployeesPending,
  createEmployee,
  title,
}) => {
  const loadLimit = 100;
  const [startFrom, setStartFrom] = useState(0);
  const [formState, setFormState] = useState(null);
  const [ungroupedEmployees, setUngroupedEmployees] = useState({});
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [newEmployeeEntities, setNewEmployeeEntities] = useState({
    group: [],
    parent: [],
  });

  const disabledGroupIds = () =>
    additionalSearchParams.excludingGroupIds.map((id) => Number(id));

  const updateEntities = (payload) => {
    setNewEmployeeEntities((prevEntities) => ({
      ...prevEntities,
      ...payload,
    }));
  };

  const onToggleEmployeeFrom = () => {
    setShowEmployeeForm(!showEmployeeForm);
    setErrors({});
    updateEntities({ parent: [] });
  };

  const showLoadMore = () => {
    return (
      formState.length > loadLimit &&
      Object.keys(ungroupedEmployees).length &&
      formState.length > Object.keys(ungroupedEmployees).length
    );
  };

  const onSubmitSuccess = () => {
    setFormState(null);
    setUngroupedEmployees({});
  };

  const handleSelectEmployee = useCallback((employee) => {
    setFormState((prevState) =>
      prevState.includes(employee.id)
        ? prevState.filter((id) => id !== employee.id)
        : [...prevState, employee.id]
    );

    setUngroupedEmployees((prevState) => {
      return { ...prevState, [employee.id]: employee };
    });
  }, []);

  const onEmployeeGroupSelect = (group) => {
    updateEntities({
      group:
        newEmployeeEntities.group.length &&
        newEmployeeEntities.group[0].id === group.id
          ? []
          : [group],
    });
  };

  const onEmployeeParentSelect = (employee) => {
    updateEntities({
      parent:
        newEmployeeEntities.parent.length &&
        newEmployeeEntities.parent[0].id === employee.id
          ? []
          : [employee],
    });
  };

  const onAddEmployeeSubmit = (values) => {
    const errors = validateEmployee(values);
    if (keys(errors).length) {
      setErrors(errors);
    } else {
      const employee = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        position: values.position,
        location: values.location,
        externalId: values.externalId,
        groupId: newEmployeeEntities.group.length
          ? newEmployeeEntities.group[0].id
          : null,
        parentId: newEmployeeEntities.parent.length
          ? newEmployeeEntities.parent[0].id
          : null,
      };

      return createEmployee(organizationId, employee)
        .then(({ entities, result }) => {
          handleSelectEmployee(entities.employees[result]);
          updateEntities({ parent: [] });
          setShowEmployeeForm(false);
          setErrors({});
        })
        .catch((err) => setErrors(mapFinalFormErrors()(err)));
    }
  };

  const retrieveEntities = (employeeIds) => {
    retrieveSimplifiedEmployees({
      ...additionalSearchParams,
      autocomplete: false,
      active: true,
      employeeIds: getChunkOfElements(employeeIds, startFrom, loadLimit),
    }).then(({ entities }) => {
      setUngroupedEmployees({
        ...ungroupedEmployees,
        ...entities.employeesAutocomplete,
      });
      setStartFrom(startFrom + loadLimit);
    });
  };

  const onLoadMore = () => {
    retrieveEntities(formState, startFrom, loadLimit);
  };

  useEffect(() => {
    if (showFormModal) {
      setShowEmployeeForm(false);
      setFormState(employeeIds);
      employeeIds.length && retrieveEntities(employeeIds);
    }
  }, [showFormModal]);

  return formState && showFormModal ? (
    <FormModal
      createResource={() => Promise.resolve(onSubmit(formState))}
      onSubmitSuccess={onSubmitSuccess}
      onHide={onHideFormModal}
      title={`Manage - ${title}`}
      show={showFormModal}
      hideFooter={showEmployeeForm}
    >
      <ManageParticipantsForm
        ungroupedEmployees={ungroupedEmployees}
        handleSelect={handleSelectEmployee}
        additionalSearchParams={additionalSearchParams}
        onLoadMore={onLoadMore}
        showLoadMore={showLoadMore}
        formState={formState}
        pending={retrieveEmployeesPending}
        newEmployeeEntities={newEmployeeEntities}
        showEmployeeForm={showEmployeeForm}
        setShowEmployeeForm={onToggleEmployeeFrom}
        disabledGroupIds={disabledGroupIds()}
        onGroupSelect={onEmployeeGroupSelect}
        onParentSelect={onEmployeeParentSelect}
        onAddEmployeeSubmit={onAddEmployeeSubmit}
        errors={errors}
      />
    </FormModal>
  ) : null;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ManageParticipantsModal);
